/**
 * `useRasterSlot` — an intel chip borrowing the map's single raster slot, and giving it
 * back.
 *
 * Two UC2 chips draw a published COG instead of a choropleth (the greening trend and the
 * land-cover change classes), and both have to behave the same way about the slot they
 * take: switch the layer on while the chip is active, restore whatever raster was on
 * before it on the way out, and leave a raster the reader picked *themselves* meanwhile
 * alone — they have overruled the chip, and having it swapped back under them is the kind
 * of thing that makes a map feel haunted.
 *
 * Both writes name the raster set explicitly rather than leaving it to the catalogue
 * register, so the swap works the same whether or not `/layers` has landed yet, and
 * `intel.layersToggled` is kept in step so `#intelReset` switches off exactly what the
 * pane switched on.
 */
import { useEffect } from 'react';

import { activeRasterOf, useAppStore } from '../../lib/state';

export function useRasterSlot(layerId: string, active: boolean) {
  const setActiveRaster = useAppStore((s) => s.setActiveRaster);
  const setIntelLayerToggled = useAppStore((s) => s.setIntelLayerToggled);

  useEffect(() => {
    if (!active) return;
    const previous = activeRasterOf(useAppStore.getState().layersOn);
    const swapped = previous ? [previous, layerId] : [layerId];
    setActiveRaster(layerId, swapped);
    setIntelLayerToggled(layerId, true);
    return () => {
      // `layersOn.has`, not `activeRasterOf`: the question is whether *this* chip's layer
      // is still drawn, which is true whether or not the catalogue register has landed.
      if (useAppStore.getState().layersOn.has(layerId)) {
        setActiveRaster(previous, [layerId]);
      }
      setIntelLayerToggled(layerId, false);
    };
  }, [layerId, active, setActiveRaster, setIntelLayerToggled]);
}

export default useRasterSlot;
